import { createTheme, alpha, TransitionsOptions } from '@mui/material/styles';
import { CreateCSSProperties } from '@mui/styles';
import Fade from '@mui/material/Fade';
import * as csstype from 'csstype';
import '@mui/x-date-pickers/themeAugmentation';

export const colors = {
  // DON'T use #C12B20, use primaryAccent color instead
  primary: '#000',
  mainAccent: '#13A9BA',
  mainAccentHover: 'rgba(19,169,186,0.09)',
  primaryAccent: '#13A9BA',
  primaryAccentHover: '#DA1616',
  secondary: '#373633',
  red: '#DC3545',
  warning: '#FFC107',
  error: '#DC3545',
  black: '#000',
  black2: '#3D3F3D',
  grey: '#A9AAAC',
  greyLight: '#DFDFDF',
  greyLight2: '#C4C4C4',
  greyLight3: '#6C757D',
  greyLight4: '#b4b4b4',
  greyLight5: '#F8F9FA',
  greyLight6: '#D7D8D9',
  greyLight7: '#F1F1F1',
  greyDark: '#364859',
  greyDark2: '#8C8C8C',
  blueDark: '#071C4B',
  blue: '#236BF6',
  green: '#5FC97B',
  green2: '#16c79c',
  greenHover: '#6FCE88',
  white: '#FFFFFF',
  whitePale: '#FAFAFA',
  disabled: '#EFEFEF',
  backgroundColor: '#F7F7FA',
  gold: '#F8A04E',
  secondaryGreyLight: '#7D859A',
  inactiveGrey: '#C1C1C1',
  serviceTypeAppointment: '#13A9BA',
  serviceTypeAppSubscription: '#16C79C',
  chipInactive: '#AFAFC0',
  courseBought: '#13A9BA',
  courseCompleted: '#16C79C',
  courseLive: '#088f6d',
};

export const borderStyles = {
  primarySolid: `1px solid ${alpha('#000', 0.3)}`,
  primarySolidHover: '1px solid transparent',
  secondarySolid: '',
  primaryDashed: `1px dashed ${alpha('#000', 0.3)}`,
  primaryDashedHover: '1px dashed transparent',
  secondaryDashed: '',
};

const transitions: TransitionsOptions = {
  duration: {
    standard: 0.3,
  },
};

export const opacity = {
  disabled: 0.6,
};

const commonBorderStyles: CreateCSSProperties = {
  color: colors.black,
  transition: `${transitions.duration?.standard}s`,
  fontWeight: 500,
  cursor: 'pointer',
  lineHeight: 1.2,
};

export const shortcuts: { [name: string]: CreateCSSProperties } = {
  borderPrimarySolid: {
    ...commonBorderStyles,
    borderBottom: borderStyles.primarySolid,

    '&:hover': {
      borderBottom: borderStyles.primarySolidHover,
    },
  },
  borderPrimaryDashed: {
    ...commonBorderStyles,
    borderBottom: borderStyles.primaryDashed,

    '&:hover': {
      borderBottom: borderStyles.primaryDashedHover,
    },
  },
  disabled: {
    opacity: opacity.disabled,
    pointerEvents: 'none' as csstype.Property.PointerEvents,
  },
  errorText: {
    fontSize: 11,
    fontWeight: 500,
    width: '100%',
    color: colors.error,
    position: 'absolute' as csstype.Property.Position,
  },
};

const fontFamily = '"Roboto", sans-serif';

const controlsRadius = 8;
const buttonRadius = 8;
const inputRadius = 4;
const spacing = 4;
const containersRadius = 8;
const circleRadius = '50%';

export const scrollStyles: CreateCSSProperties = {
  '&::-webkit-scrollbar': {
    width: 8,
    height: 8,
  },

  '&::-webkit-scrollbar-track': {
    borderRadius: containersRadius,
    backgroundColor: colors.greyLight7,
    margin: 1,
  },

  '&::-webkit-scrollbar-thumb': {
    background: colors.greyLight3,
    borderRadius: containersRadius,
  },

  '&::-webkit-scrollbar-thumb:hover': {
    background: colors.greyDark,
  },

  scrollbarColor: `${colors.greyLight3} ${colors.greyLight7}`,
  scrollbarWidth: 'thin',
};

export default createTheme({
  spacing,
  opacity,
  shortcuts,
  transitions,
  borderStyles,
  radius: {
    control: controlsRadius,
    container: containersRadius,
    circle: circleRadius,
  },
  palette: {
    colors,
    primary: {
      main: colors.primaryAccent,
    },
    secondary: {
      main: colors.secondary,
    },
    error: {
      main: colors.error,
    },
  },
  typography: {
    fontFamily,
  },
  components: {
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
    },
    MuiTooltip: {
      defaultProps: {
        placement: 'top',
        slots: {
          transition: Fade,
        },
        slotProps: {
          transition: {
            exit: false,
          },
        },
        leaveDelay: 0,
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: '#000',
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          paddingRight: 24,
          paddingLeft: 100,
        },
        regular: {
          minHeight: 56,
          '@media (min-width: 600px)': {
            minHeight: '56px',
          },
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          boxShadow: 'none',
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: buttonRadius,
          fontWeight: 700,
          textTransform: 'none',

          boxSizing: 'border-box',

          '&.Mui-disabled': {
            opacity: 0.6,
          },
        },
        text: {
          '&:hover': {
            boxShadow: 'none',
            borderRadius: '25px',
          },
        },
        sizeSmall: {
          height: 36,
          padding: `0 ${spacing * 4}px`,
          fontSize: 12,
        },
        sizeLarge: {
          height: 40,
          padding: `0 ${spacing * 6}px`,
          fontSize: 14,
        },
        contained: {
          // boxShadow: 'none',
          color: colors.greyDark,
          fontWeight: 500,
          boxShadow: '0px 1px 13px rgba(162,162,162,0.58)',
          border: '1px solid #ffffff',

          '&:hover': {
            backgroundColor: colors.greyLight4,
            boxShadow: '0px 1px 13px rgba(19,169,186,0.26)',
          },
          '&.Mui-disabled': {
            color: alpha(colors.greyDark, 0.5),
            backgroundColor: colors.disabled,
          },

          '&.MuiButton-colorPrimary': {
            backgroundColor: colors.primaryAccent,
            color: colors.white,
            boxShadow: '0px 1px 13px rgba(168,168,168,0.58)',

            '&:hover': {
              boxShadow: '0px 1px 13px rgba(19,169,186,0.26)',

              backgroundColor: colors.primaryAccent,
            },

            '&.Mui-disabled': {
              color: colors.whitePale,
              backgroundColor: colors.primaryAccent,
              opacity: 0.6,
            },
          },

          '&.MuiButton-colorSecondary': {
            backgroundColor: colors.green,

            '&:hover': {
              backgroundColor: colors.greenHover,
            },

            '&.Mui-disabled': {
              color: colors.whitePale,
              backgroundColor: colors.green,
              opacity: 0.6,
            },
          },
        },
        outlined: {
          borderColor: colors.black,

          '&.MuiButton-colorPrimary': {
            borderColor: colors.red,
            color: colors.red,

            '&:hover': {
              backgroundColor: colors.primaryAccentHover,
              borderColor: colors.primaryAccentHover,
              color: 'white',
            },

            '&.Mui-disabled': {
              borderColor: colors.red,
              color: colors.red,
              opacity: 0.6,
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          position: 'relative',

          '& p': {
            // helper text
            marginTop: 0,
            position: 'absolute',
            top: '108%',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.white,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.black,
            borderWidth: 1,
          },
        },
        notchedOutline: {
          top: 0,
          borderRadius: controlsRadius,
          borderColor: colors.black,
          backgroundColor: '#fff',

          '& legend': {
            display: 'none',
          },
        },
        input: {
          paddingTop: 0,
          paddingLeft: spacing * 5,
          paddingBottom: 0,
          height: 48,
        },
        sizeSmall: {
          paddingTop: 0,
          paddingBottom: 0,
          height: 36,
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.black}`,

          '&.Mui-error': {
            borderColor: colors.error,
          },
        },
        input: {
          padding: 0,
          height: '100%',

          '&::before, &::after': {
            borderBottom: 'none !important',
          },
        },
        underline: {
          '&::before, &::after': {
            borderBottom: 'none !important',
          },

          '&.Mui-disabled:before': {
            borderBottomStyle: 'solid',
            borderColor: colors.greyLight,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          minHeight: 40,
          fontSize: 15,
          lineHeight: '20px',

          '&.Mui-disabled': {
            color: '#000',
            pointerEvents: 'none',
          },
          borderRadius: controlsRadius,
          border: controlsRadius,
        },
        input: {
          backgroundColor: colors.backgroundColor,
          color: colors.black2,
          // border: `1px solid ${colors.grey}`,
          borderRadius: controlsRadius,
          // '&:focus': {
          //   border: `1px solid ${colors.primaryAccent}`
          // },

          // '&[aria-invalid="true"]:not(:focus)' : {
          //   border: `1px solid ${colors.red}`,
          // }
        },

        multiline: {
          height: 'auto',
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        positionStart: {
          margin: '0px',
          marginLeft: '0px',
          marginRight: '0px;',
          paddingLeft: '0px',
          '&:not(.MuiInputAdornment-hiddenLabel)': {
            marginTop: '0!important',
          },
          '& span': {
            position: 'absolute',
            left: '12px',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: 11,
          color: 'red',
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: colors.backgroundColor,
          borderRadius: inputRadius,
          border: '1px solid transparent',
          borderTopLeftRadius: inputRadius,
          borderTopRightRadius: inputRadius,
          '&.Mui-error': {
            color: 'white',
            border: `1px solid ${colors.red}`,
            borderRadius: inputRadius,
          },
          '&.Mui-focused': {
            color: 'white',
            borderRadius: inputRadius,
            backgroundColor: colors.backgroundColor,
            '& .MuiInputAdornment-positionStart > svg': {
              color: colors.primaryAccent,
              fill: colors.primaryAccent,
            },
          },
          '&:hover': {
            backgroundColor: colors.backgroundColor,
          },
        },
        adornedStart: {
          paddingLeft: '0',

          '& .MuiFilledInput-input': {
            paddingLeft: '12px',
          },
        },
        input: {
          padding: '0px',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: controlsRadius,
        },
        select: {
          zIndex: 1,
          borderRadius: controlsRadius,

          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
        icon: {
          zIndex: 1,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.black,

          '&.Mui-checked': {
            color: colors.primaryAccent,
          },
        },
        colorSecondary: {
          '&.Mui-checked': {
            color: colors.primaryAccent,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 'auto',
          padding: 0,

          '&.Mui-expanded': {
            minHeight: 'auto',
          },
        },
        content: {
          alignItems: 'center',
          margin: 0,
          width: '100%',
          padding: `0 0 0 ${spacing * 9}px`,

          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: colors.greyLight5,
          padding: spacing * 5,
          border: `1px solid ${colors.greyLight}`,
          boxShadow: 'none',

          '&.Mui-expanded': {
            margin: 0,

            '&:last-child': {
              marginBottom: 'initial',
            },
          },
        },
        rounded: {
          borderRadius: containersRadius,

          '&::before': {
            display: 'none',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          lineHeight: 1,
          ...shortcuts.errorText,
        } as any,
        contained: {
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          flexWrap: 'nowrap',
          backgroundColor: 'white',
          padding: spacing * 4,
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.16)',
        },
        message: {
          fontFamily,
          padding: 0,
          color: 'black',
          fontSize: 15,
          fontWeight: 500,
        },
        action: {
          paddingLeft: spacing * 4,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        elevation1: {
          // boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.16)',
          boxShadow: '0px 0px 10px rgb(160 160 160 / 28%)',
        },
        elevation3: {
          boxShadow: '0px 4px 12px rgba(91, 91, 91, 0.25)',
        },
        elevation4: {
          boxShadow: '0px 4px 12px rgba(91, 91, 91, 0.25)',
        },
        elevation8: {
          boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.08)',
        },
        rounded: {
          borderRadius: containersRadius,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 30,
          height: 18,
          padding: 0,
        },
        switchBase: {
          padding: 0,
          top: 2,
          left: 3,

          '& > span': {
            width: '95%',
          },
        },
        colorSecondary: {
          color: 'white',
          '&.Mui-checked': {
            color: 'white',
            left: -7,
          },
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: colors.primaryAccent,
            opacity: 1,
          },
        },
        track: {
          borderRadius: controlsRadius + 2,
        },
        thumb: {
          width: 14,
          height: 14,
          borderRadius: 10,
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderRadius: containersRadius,
          overflow: 'hidden',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.Mui-hover:hover': {
            backgroundColor: alpha(colors.primaryAccent, 0.04),
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: `6px 0 6px ${spacing * 4}px`,
          color: colors.primary,
          fontSize: 15,
          height: 54,
          borderColor: colors.greyLight,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',

          '&:last-of-type': {
            paddingRight: spacing * 4,
          },

          '& *': {
            whiteSpace: 'nowrap',
          },
        },
        head: {
          padding: `${spacing * 2}px ${spacing * 4}px`,
          backgroundColor: colors.greyLight5,
          height: 34,
          borderColor: colors.greyLight,
          borderWidth: '1px 0 1px 0',
          color: colors.greyDark,
          fontWeight: 400,
          whiteSpace: 'nowrap',
          borderStyle: 'none',
          borderBottom: `1px solid ${colors.greyLight}`,
          '&:first-child': {},

          '&:last-child': {},
        },
        stickyHeader: {
          backgroundColor: colors.greyLight5,
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: spacing * 4,
          overflow: 'hidden',
        },
        toolbar: {
          border: `1px solid ${colors.greyLight}`,
          borderRadius: containersRadius,
          minHeight: 'auto',
          height: 42,
          backgroundColor: colors.greyLight5,
        },
      },
    },
    MuiPickerDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: colors.primaryAccent,

            '&:hover': {
              backgroundColor: colors.primaryAccent,
            },
          },
          '&.today': {
            color: colors.primaryAccent,
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          color: colors.greyDark,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: containersRadius,
          marginBottom: spacing,

          '&.Mui-selected': {
            backgroundColor: 'transparent',

            '&:hover': {
              backgroundColor: colors.greyLight5,
            },
          },
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: colors.mainAccentHover,
            color: colors.mainAccent,
            borderRadius: 0,
            fontWeight: 500,

            //       width: '97%',
            //       borderRadius: '0 8px 8px 0',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: colors.primaryAccent,

          '&.Mui-checked': {
            color: colors.primaryAccent,
          },
        },
        colorSecondary: {
          '&.Mui-checked': {
            color: colors.primaryAccent,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.greyLight5,
          border: `1px solid ${colors.greyLight}`,
          boxShadow: 'none',
          borderRadius: containersRadius,
          padding: spacing * 6,
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          paddingTop: spacing * 5,
          display: 'block',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: `${spacing * 2}px ${spacing * 5}px ${spacing * 5}px`,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: spacing * 6,
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (min-width: 600px)': {
            paddingLeft: 20,
            paddingRight: 20,
          },
        },
      },
    },
  },
});
